// StudioObjectives — the Learning objectives page (pen "Studio · Page ·
// Learning objectives"), under the material's name (AboutPage.tsx): a
// sentence, filter chips that are also the legend, and the coverage matrix:
// the outline's parts as rows, the objectives as columns (titles at 45°,
// 26 px each; more than fit scroll sideways while the part names and the
// State label stay, with a fade at the right edge). Pointing at a column
// (or focusing its title) tints it and shows a card: the whole objective,
// where it is taught, checked or planned, and one next step. The foot says
// where the objectives come from and adds one (at the ceiling, the Limit
// hint instead). No objectives: it says so, and offers to add one.
//
// Pure: the parts and the objectives come in (coverageMatrix.ts); an add or
// a removal goes out as its own intent. Local state is view state only: the
// chip chosen, the column pointed at, the scroll edge, the add draft.

export { StudioObjectives };

import * as Limits from "@bilda/core/limits";
import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
    type KeyboardEvent,
    type ReactNode,
    type UIEvent,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import type { ProjectObjectives } from "../../app/ports/project.js";
import { Icon } from "../Icon/Icon.js";
import { kindIcon } from "../StudioEditor/Outline.js";
import type { OutlineRow } from "../StudioEditor/studioPages.js";
import "./StudioObjectives.css";
import {
    COVERAGE_STATES,
    type CoverageColumn,
    type CoverageFilter,
    type CoverageMark,
    type CoverageRow,
    type CoverageState,
    columnsOf,
    countsOf,
    nextStepOf,
    rowsOf,
    shownColumns,
} from "./coverageMatrix.js";

// Layout constants of the matrix (pen frames.json): the part column, one
// objective column, a row; where the card sits under the header.
const COLUMN = 26;
const PART_COLUMN = 176;
const ROW = 30;
const HEADER = 118;
const CARD_WIDTH = 250;

const StudioObjectives = ({
    outline,
    objectives,
    online,
    onAdd,
    onRemove,
}: {
    // The outline's rows, unfolded (studioPages.outlineRows).
    readonly outline: readonly OutlineRow[];
    // Undefined while the first answer is on its way.
    readonly objectives: ProjectObjectives | undefined;
    readonly online: boolean;
    readonly onAdd: (statement: string) => void;
    readonly onRemove: (objectiveId: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const [filter, setFilter] = useState<CoverageFilter>("all");
    const [pointed, setPointed] = useState<string | null>(null);
    const label = useStateLabel();
    if (objectives === undefined) return null;
    const columns = columnsOf(objectives.objectives);
    const full = columns.length >= objectives.max;
    const foot = (
        <ObjectivesFoot
            columns={columns}
            full={full}
            max={objectives.max}
            online={online}
            onAdd={onAdd}
        />
    );
    if (columns.length === 0)
        return (
            <div className="bilda-objectives">
                <p className="bilda-objectives__sentence">
                    {t`No learning objectives yet. Add one, and this page shows which parts teach it and which quiz checks it.`}
                </p>
                {foot}
            </div>
        );
    const counts = countsOf(columns);
    const covered = counts.get("covered") ?? 0;
    const none = counts.get("no_part_yet") ?? 0;
    const total = columns.length;
    const shown = shownColumns(columns, filter);
    const rows = rowsOf(outline, shown, objectives.within);
    return (
        <div className="bilda-objectives">
            <p className="bilda-objectives__sentence">
                {t`${plural(total, {
                    one: `${covered} of # objective is taught and checked in a quiz.`,
                    other: `${covered} of # objectives are taught and checked in a quiz.`,
                })}`}
                {none === 0
                    ? null
                    : ` ${t`${plural(none, { one: "# has no part yet.", other: "# have no part yet." })}`}`}
            </p>
            <fieldset className="bilda-objectives__chips">
                <legend className="bilda-objectives__hidden">{t`Show`}</legend>
                <Chip
                    chosen={filter === "all"}
                    label={t`All`}
                    count={total}
                    onChoose={() => setFilter("all")}
                />
                {COVERAGE_STATES.map((s) => (
                    <Chip
                        key={s}
                        state={s}
                        chosen={filter === s}
                        label={label(s)}
                        count={counts.get(s) ?? 0}
                        onChoose={() => setFilter(s)}
                    />
                ))}
            </fieldset>
            <Matrix
                columns={shown}
                rows={rows}
                pointed={pointed}
                onPoint={setPointed}
                online={online}
                onRemove={onRemove}
            />
            <ul className="bilda-objectives__legend">
                <li>
                    <Mark mark="taught" summary={false} />
                    {t`Taught`}
                </li>
                <li>
                    <Mark mark="checked" summary={false} />
                    {t`Checked in a quiz`}
                </li>
                <li>
                    <Mark mark="planned" summary={false} />
                    {t`Planned`}
                </li>
            </ul>
            <p className="bilda-objectives__note">
                {t`Grey marks on a module sum up its parts. Point at a column to read the whole objective.`}
            </p>
            {foot}
        </div>
    );
};

// The chips' words for a state (a hook: the words follow the locale).
const useStateLabel = (): ((s: CoverageState) => string) => {
    const { t } = useLingui();
    return (s) => {
        switch (s) {
            case "no_part_yet":
                return t`No part yet`;
            case "not_checked":
                return t`Not checked`;
            case "planned":
                return t`Planned`;
            case "covered":
                return t`Taught and checked`;
            default:
                return exhaustSwitchCase(s);
        }
    };
};

const StateSquare = ({ state }: { readonly state: CoverageState }) => (
    <span className="bilda-objectives__square" data-state={state} />
);

const Chip = ({
    state,
    chosen,
    label,
    count,
    onChoose,
}: {
    readonly state?: CoverageState;
    readonly chosen: boolean;
    readonly label: string;
    readonly count: number;
    readonly onChoose: () => void;
}): ReactNode => (
    <button
        type="button"
        className="bilda-objectives__chip"
        aria-pressed={chosen}
        onClick={onChoose}
    >
        {state === undefined ? null : <StateSquare state={state} />}
        {label}
        <span className="bilda-objectives__count">{count}</span>
    </button>
);

const Mark = ({
    mark,
    summary,
}: {
    readonly mark: CoverageMark;
    readonly summary: boolean;
}): ReactNode => {
    switch (mark) {
        case "taught":
            return (
                <span
                    className="bilda-objectives__dot"
                    data-summary={summary}
                />
            );
        case "checked":
            return (
                <span className="bilda-objectives__mark" data-summary={summary}>
                    <Icon name="circle-check" size="xs" />
                </span>
            );
        case "planned":
            return (
                <span
                    className="bilda-objectives__mark"
                    data-planned="true"
                    data-summary={summary}
                >
                    <Icon name="circle-dashed" size="xs" />
                </span>
            );
        case "none":
            return null;
        default:
            return exhaustSwitchCase(mark);
    }
};

// A cell's words for its mark (none: no words).
const useMarkLabel = (): ((mark: CoverageMark) => string | undefined) => {
    const { t } = useLingui();
    return (mark) => {
        switch (mark) {
            case "taught":
                return t`Taught`;
            case "checked":
                return t`Checked in a quiz`;
            case "planned":
                return t`Planned`;
            case "none":
                return undefined;
            default:
                return exhaustSwitchCase(mark);
        }
    };
};

// The short label of a column title: its number and the objective, cut.
const SHORT = 28;
const shortOf = (statement: string): string =>
    statement.length > SHORT ? `${statement.slice(0, SHORT - 1)}…` : statement;

// Where the scrolled columns stand: more to the right, how far scrolled,
// how wide the view is.
type Edge = {
    readonly more: boolean;
    readonly left: number;
    readonly width: number;
};
const edgeOf = (el: HTMLDivElement): Edge => ({
    more: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    left: el.scrollLeft,
    width: el.clientWidth,
});

const Matrix = ({
    columns,
    rows,
    pointed,
    onPoint,
    online,
    onRemove,
}: {
    readonly columns: readonly CoverageColumn[];
    readonly rows: readonly CoverageRow[];
    readonly pointed: string | null;
    readonly onPoint: (id: string | null) => void;
    readonly online: boolean;
    readonly onRemove: (objectiveId: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const label = useStateLabel();
    const markLabel = useMarkLabel();
    const scroller = useRef<HTMLDivElement>(null);
    // View state: whether more columns lie to the right, and how far it has
    // scrolled (to place the card at its column).
    const [edge, setEdge] = useState<Edge>({ more: false, left: 0, width: 0 });
    const count = columns.length;
    useLayoutEffect(() => {
        if (scroller.current !== null && count >= 0)
            setEdge(edgeOf(scroller.current));
    }, [count]);
    const at = columns.findIndex((c) => c.objective.id === pointed);
    const column = columns[at];
    const point = (id: string) => () => onPoint(id);
    const cardLeft = (() => {
        const right = PART_COLUMN + (at + 1) * COLUMN - edge.left + 6;
        return right + CARD_WIDTH > edge.width
            ? Math.max(
                  PART_COLUMN,
                  PART_COLUMN + at * COLUMN - edge.left - CARD_WIDTH - 6,
              )
            : right;
    })();
    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: leaving the matrix only clears the column pointed at (view state)
        <div
            className="bilda-objectives__matrix"
            onMouseLeave={() => onPoint(null)}
        >
            <div
                ref={scroller}
                className="bilda-objectives__scroll"
                onScroll={(e: UIEvent<HTMLDivElement>) =>
                    setEdge(edgeOf(e.currentTarget))
                }
            >
                <table
                    className="bilda-objectives__table"
                    style={{
                        width: `max(100%, ${PART_COLUMN + count * COLUMN + HEADER}px)`,
                    }}
                >
                    <thead>
                        <tr>
                            <th className="bilda-objectives__part" scope="col">
                                <span className="bilda-objectives__hidden">{t`Part`}</span>
                            </th>
                            {columns.map((c) => (
                                <th
                                    key={c.objective.id}
                                    scope="col"
                                    className="bilda-objectives__head"
                                    data-pointed={c.objective.id === pointed}
                                    onMouseEnter={point(c.objective.id)}
                                >
                                    <button
                                        type="button"
                                        className="bilda-objectives__title"
                                        title={c.objective.statement}
                                        onFocus={point(c.objective.id)}
                                    >
                                        <span className="bilda-objectives__number">
                                            {c.number}
                                        </span>{" "}
                                        {shortOf(c.objective.statement)}
                                    </button>
                                </th>
                            ))}
                            <th className="bilda-objectives__rest" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key}>
                                <th
                                    scope="row"
                                    className="bilda-objectives__part"
                                    title={r.title}
                                    data-part={r.id}
                                    data-summary={r.summary}
                                >
                                    <span
                                        className="bilda-objectives__part-name"
                                        style={{
                                            paddingInlineStart: r.depth * 14,
                                        }}
                                    >
                                        <Icon
                                            name={kindIcon(r.kind)}
                                            size="xs"
                                        />
                                        <span className="bilda-objectives__part-title">
                                            {r.title}
                                        </span>
                                    </span>
                                </th>
                                {r.marks.map((m, i) => {
                                    const c = columns[i];
                                    const id = c?.objective.id ?? "";
                                    return (
                                        <td
                                            key={id}
                                            className="bilda-objectives__cell"
                                            data-pointed={id === pointed}
                                            aria-label={markLabel(m)}
                                            onMouseEnter={point(id)}
                                        >
                                            <Mark
                                                mark={m}
                                                summary={r.summary}
                                            />
                                        </td>
                                    );
                                })}
                                <td className="bilda-objectives__rest" />
                            </tr>
                        ))}
                        <tr className="bilda-objectives__state-row">
                            <th scope="row" className="bilda-objectives__part">
                                {t`STATE`}
                            </th>
                            {columns.map((c) => (
                                <td
                                    key={c.objective.id}
                                    className="bilda-objectives__cell"
                                    data-pointed={c.objective.id === pointed}
                                    aria-label={label(c.state)}
                                    onMouseEnter={point(c.objective.id)}
                                >
                                    <StateSquare state={c.state} />
                                </td>
                            ))}
                            <td className="bilda-objectives__rest" />
                        </tr>
                    </tbody>
                </table>
            </div>
            {edge.more ? <span className="bilda-objectives__fade" /> : null}
            {column === undefined ? null : (
                <ColumnCard
                    column={column}
                    rows={rows}
                    left={cardLeft}
                    top={HEADER + ROW * 3 + 4}
                    online={online}
                    onRemove={() => {
                        onPoint(null);
                        onRemove(column.objective.id);
                    }}
                />
            )}
        </div>
    );
};

const titlesOf = (rows: readonly CoverageRow[], ids: readonly string[]) =>
    rows
        .filter((r) => ids.includes(r.id))
        .map((r) => r.title)
        .filter((title, i, all) => all.indexOf(title) === i)
        .join(", ");

const ColumnCard = ({
    column,
    rows,
    left,
    top,
    online,
    onRemove,
}: {
    readonly column: CoverageColumn;
    readonly rows: readonly CoverageRow[];
    readonly left: number;
    readonly top: number;
    readonly online: boolean;
    readonly onRemove: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const { objective } = column;
    const taught = titlesOf(rows, objective.taught);
    const checked = titlesOf(rows, objective.checked);
    const planned = titlesOf(rows, objective.planned);
    const step = nextStepOf(column, rows);
    const next = ((): string | undefined => {
        switch (step.type) {
            case "add_part":
                return t`Add a part that teaches it`;
            case "add_question":
                return t`Add a question to ${step.quiz}`;
            case "add_quiz":
                return t`Add a quiz that checks it`;
            case "none":
                return undefined;
            default:
                return exhaustSwitchCase(step);
        }
    })();
    return (
        <section
            className="bilda-objectives__card"
            style={{ left, top, width: CARD_WIDTH }}
            aria-label={objective.statement}
        >
            <p className="bilda-objectives__card-title">
                {column.number} · {objective.statement}
            </p>
            {taught === "" ? null : (
                <p className="bilda-objectives__card-line">
                    <Mark mark="taught" summary={false} />
                    {t`Taught in ${taught}`}
                </p>
            )}
            {checked === "" ? null : (
                <p className="bilda-objectives__card-line">
                    <Mark mark="checked" summary={false} />
                    {t`Checked in ${checked}`}
                </p>
            )}
            {taught === "" && checked !== "" ? (
                <p className="bilda-objectives__card-line" data-warning="true">
                    <StateSquare state="not_checked" />
                    {t`Not taught in a part yet`}
                </p>
            ) : null}
            {taught !== "" && checked === "" ? (
                <p className="bilda-objectives__card-line" data-warning="true">
                    <StateSquare state="not_checked" />
                    {t`Not checked in a quiz`}
                </p>
            ) : null}
            {planned === "" || taught !== "" || checked !== "" ? null : (
                <p className="bilda-objectives__card-line">
                    <Mark mark="planned" summary={false} />
                    {t`Planned in ${planned}`}
                </p>
            )}
            {column.state === "no_part_yet" ? (
                <p className="bilda-objectives__card-line" data-warning="true">
                    <StateSquare state="no_part_yet" />
                    {t`No part teaches it yet`}
                </p>
            ) : null}
            {next === undefined ? null : (
                <p className="bilda-objectives__card-next">{next}</p>
            )}
            {online ? (
                <button
                    type="button"
                    className="bilda-objectives__card-remove"
                    onClick={onRemove}
                >
                    <Icon name="trash" size="xs" />
                    {t`Remove objective`}
                </button>
            ) : null}
        </section>
    );
};

const ObjectivesFoot = ({
    columns,
    full,
    max,
    online,
    onAdd,
}: {
    readonly columns: readonly CoverageColumn[];
    readonly full: boolean;
    readonly max: number;
    readonly online: boolean;
    readonly onAdd: (statement: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    // View state: the author is writing a new objective (the draft).
    const [draft, setDraft] = useState<string | null>(null);
    const sources = columns
        .flatMap((c) => c.objective.from.map((s) => s.title))
        .filter((title, i, all) => all.indexOf(title) === i)
        .join(", ");
    const keep = () => {
        const statement = (draft ?? "").trim();
        if (statement !== "") onAdd(statement);
        setDraft("");
    };
    const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            keep();
        } else if (e.key === "Escape") {
            e.stopPropagation();
            setDraft(null);
        }
    };
    const add = ((): ReactNode => {
        if (!online) return null;
        if (full)
            return (
                <span className="bilda-objectives__limit">
                    {t`A material holds up to ${max} objectives`}
                </span>
            );
        if (draft !== null)
            return (
                <input
                    className="bilda-objectives__input"
                    aria-label={t`Add an objective`}
                    placeholder={t`What should the learner be able to do?`}
                    maxLength={Limits.LONG_TEXT_MAX}
                    // biome-ignore lint/a11y/noAutofocus: the author just asked to write one
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.currentTarget.value)}
                    onKeyDown={onKey}
                    onBlur={() => {
                        if ((draft ?? "").trim() === "") setDraft(null);
                    }}
                />
            );
        return (
            <button
                type="button"
                className="bilda-objectives__add"
                onClick={() => setDraft("")}
            >
                <Icon name="plus" size="xs" />
                {t`Add an objective`}
            </button>
        );
    })();
    return (
        <p className="bilda-objectives__foot">
            {sources === "" ? null : (
                <span className="bilda-objectives__from">{t`From ${sources}`}</span>
            )}
            {add}
        </p>
    );
};
